import { useState, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send, Paperclip, FileText, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { messagesAPI, uploadsAPI } from '../../services/api';
import useAuthStore from '../../context/authStore';

import BackButton from '../../components/common/BackButton';
export default function MessageThreadPage() {
  const { id } = useParams();
  const { user } = useAuthStore();
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sendError, setSendError] = useState('');
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const MAX_UPLOAD_SIZE = 50 * 1024 * 1024; // 50MB
  const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
    'text/csv',
  ];

  const uploadWithProgress = (uploadUrl, file, onProgress) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    };
    return new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve({ xhr });
        else reject(new Error('Upload failed with status ' + xhr.status));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
  };  

  const handleFiles = async (files) => {
    if (!files || !files.length) return;

    for (const file of Array.from(files)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: File type not allowed`);
        continue;
      }
      if (file.size > MAX_UPLOAD_SIZE) {
        toast.error(`${file.name}: File too large (max 50MB)`);
        continue;
      }

      const id = `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
      setAttachments((a) => [...a, { id, file, fileName: file.name, fileUrl: null, key: null, fileSize: file.size, fileType: file.type, progress: 0, status: 'uploading', xhr: null }]);

      try {
        setUploading(true);
        const res = await uploadsAPI.getPresignedUrl({ fileName: file.name, fileType: file.type });
        const { uploadUrl, key, fileUrl } = res.data;
        setAttachments((a) => a.map(att => att.id === id ? { ...att, key, fileUrl } : att));

        const { xhr } = await uploadWithProgress(uploadUrl, file, (percent) => {
          setAttachments((a) => a.map(att => att.id === id ? { ...att, progress: percent } : att));
        });

        setAttachments((a) => a.map(att => att.id === id ? { ...att, progress: 100, status: 'uploaded', xhr: null } : att));
      } catch (err) {
        console.error('Upload failed', err);
        setAttachments((a) => a.map(att => att.id === id ? { ...att, status: 'error', xhr: null } : att));
        toast.error('Upload failed for ' + file.name);
      } finally {
        setUploading(false);
      }
    }
  };

  const cancelUpload = (id) => {
    const att = attachments.find(a => a.id === id);
    if (att && att.xhr) {
      try { att.xhr.abort(); } catch (e) { }
    }
    setAttachments(a => a.map(att => att.id === id ? { ...att, status: 'cancelled', progress: 0, xhr: null } : att));
  };

  const retryUpload = async (id) => {
    const att = attachments.find(a => a.id === id);
    if (!att) return;
    setAttachments(a => a.map(x => x.id === id ? { ...x, status: 'uploading', progress: 0 } : x));
    try {
      const res = await uploadsAPI.getPresignedUrl({ fileName: att.fileName, fileType: att.fileType });
      const { uploadUrl, key, fileUrl } = res.data;
      setAttachments(a => a.map(x => x.id === id ? { ...x, key, fileUrl } : x));
      const { xhr } = await uploadWithProgress(uploadUrl, att.file, (percent) => {
        setAttachments(a => a.map(x => x.id === id ? { ...x, progress: percent } : x));
      });
      setAttachments(a => a.map(x => x.id === id ? { ...x, status: 'uploaded', progress: 100, xhr: null } : x));
    } catch (err) {
      console.error('Retry failed', err);
      setAttachments(a => a.map(x => x.id === id ? { ...x, status: 'error', xhr: null } : x));
      toast.error('Retry failed');
    }
  };  

  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const deleteAttachment = async (id) => {
    const att = attachments.find(a => a.id === id);
    if (!att) return;
    if (att.key && !window.confirm('Permanently delete this uploaded file?')) return;
    try {
      if (att.key) await uploadsAPI.delete(att.key);
      setAttachments((a) => a.filter((x) => x.id !== id));
      toast.success('File deleted');
    } catch (err) {
      console.error('Delete failed', err);
      toast.error('Failed to delete file');
    }
  }; 

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const completedUploads = attachments.filter(a => a.status === 'uploaded').length;
  const overallProgress = useMemo(() => {
    const uploadAtts = attachments.filter(a => ['uploading','uploaded','error','cancelled'].includes(a.status));
    if (uploadAtts.length === 0) return 100;
    const sum = uploadAtts.reduce((s, a) => s + (a.progress || 0), 0);
    return Math.round(sum / uploadAtts.length);
  }, [attachments]);

  const { data: thread, isLoading } = useQuery({
    queryKey: ['message-thread', id],
    queryFn: () => messagesAPI.getById(id),
    select: (res) => res.data.thread,
  });

  const replyMutation = useMutation({
    mutationFn: (payload) => messagesAPI.reply(id, payload),
    onSuccess: () => {
      setNewMessage('');
      setAttachments([]);
      queryClient.invalidateQueries(['message-thread', id]);
      toast.success('Reply sent');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send reply');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSendError('');
    if (!newMessage.trim() && attachments.length === 0) return;

    if (attachments.some(a => a.status === 'uploading')) {
      setSendError('Please wait for uploads to finish or cancel them.');
      return;
    }

    if (attachments.some(a => a.status === 'error' || a.status === 'cancelled')) {
      setSendError('One or more file uploads failed or were cancelled. Retry or remove them before sending.');
      return;
    }

    const attachmentsPayload = attachments.filter(a => a.status === 'uploaded').map(a => ({ fileName: a.fileName, fileUrl: a.fileUrl, key: a.key, fileSize: a.fileSize, fileType: a.fileType }));

    replyMutation.mutate({ content: newMessage, attachments: attachmentsPayload });
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-500">Thread not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton fallbackTo={"/messages"} plainClassName="inline-flex items-center gap-2 text-secondary-600 hover:text-secondary-900" />

      <div className="card">
        <div className="card-header">
          <h1 className="text-xl font-semibold text-secondary-900">{thread.subject}</h1>
          <p className="text-sm text-secondary-500 mt-1">
            {thread.projectId?.name || (thread.participants || []).filter(p => p._id !== user?.id).slice(0,3).map(p => `${p.firstName} ${p.lastName}`).join(', ')}{thread.participants?.length ? ` • ${thread.participants.length} participant${thread.participants.length > 1 ? 's' : ''}` : ''}
          </p>
        </div>

        {/* Messages */}
        <div className="p-6 space-y-6 max-h-[500px] overflow-y-auto">
          {thread.messages?.map((message, index) => {
            const isOwn = message.userId?._id === user?.id;
            return (
              <div key={index} className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}>
                <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-medium">
                    {message.userId?.firstName?.[0]}{message.userId?.lastName?.[0]}
                  </span>
                </div>
                <div className={`max-w-[70%] ${isOwn ? 'text-right' : ''}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-secondary-900 text-sm">
                      {message.userId?.firstName} {message.userId?.lastName}
                    </span>
                    <span className="text-xs text-secondary-500">
                      {new Date(message.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className={`p-3 rounded-lg ${isOwn ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-900'}`}>
                    {message.content}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {message.attachments.map((att) => (
                          <div key={att.key} className="bg-secondary-50 p-2 rounded">
                            <a href={att.fileUrl} target="_blank" rel="noreferrer" className={`${isOwn ? 'text-white' : 'text-primary-600'} text-sm`}>{att.fileName}</a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply form */}
        <div className="p-4 border-t border-secondary-200">
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="flex gap-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="input flex-1"
              />
              <button
                type="submit"
                disabled={replyMutation.isPending || attachments.some(a => a.status === 'uploading') || (!newMessage.trim() && attachments.length === 0)}
                className="btn-primary"
              >
                <Send size={18} />
                Send
              </button>
            </div>

            {sendError && (
              <div className="p-3 bg-red-50 border border-red-100 rounded text-red-700 text-sm mt-2">
                {sendError} <button onClick={() => setSendError('')} className="underline ml-2">Dismiss</button>
              </div>
            )}

            <div className="mt-2 flex items-center gap-2">
              <input ref={fileInputRef} type="file" multiple onChange={(e) => handleFiles(e.target.files)} className="hidden" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-secondary-50 flex items-center gap-1"><Paperclip size={14}/> Attach files</button>
              <div className="text-sm text-secondary-500">Max 50MB per file • Supported: jpg, png, pdf, docx</div>
            </div>

            {attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {attachments.map((att) => (
                  <div key={att.id} className="flex items-center gap-2 p-2 bg-secondary-50 rounded-lg">
                    <div className="text-secondary-400"><FileText size={16} /></div>
                    <div className="flex-1">
                      <a href={att.fileUrl || '#'} target="_blank" rel="noreferrer" className="text-sm text-primary-600 truncate">{att.fileName}</a>
                      <div className="text-xs text-secondary-400">{formatFileSize(att.fileSize)}</div>
                      {att.status === 'uploading' && (
                        <div className="mt-1">
                          <div className="h-2 bg-secondary-200 rounded overflow-hidden">
                            <div className="h-2 bg-primary-600" style={{ width: `${att.progress}%` }} />
                          </div>
                          <div className="text-xs text-secondary-500 mt-1">{att.progress}%</div>
                        </div>
                      )}
                      {att.status === 'error' && <div className="text-xs text-red-500 mt-1">Upload failed</div>}
                      {att.status === 'cancelled' && <div className="text-xs text-secondary-500 mt-1">Cancelled</div>}
                    </div>

                    {att.status === 'uploading' && (
                      <button type="button" onClick={() => cancelUpload(att.id)} className="text-secondary-600 hover:text-secondary-800">Cancel</button>
                    )}
                    {(att.status === 'error' || att.status === 'cancelled') && (
                      <button type="button" onClick={() => retryUpload(att.id)} className="text-secondary-600 hover:text-secondary-800">Retry</button>
                    )}
                    <button type="button" onClick={() => removeAttachment(att.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                  </div>
                ))}

                {attachments.some(a => a.status === 'uploading') && (
                  <div className="ml-4 w-48">
                    <div className="text-xs text-secondary-500 mb-1">Uploading files</div>
                    <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
                      <div className="bg-primary-600 h-2" style={{ width: `${overallProgress}%` }} />
                    </div>
                    <div className="text-xs text-secondary-400 text-right">{completedUploads}/{attachments.length} • {overallProgress}%</div>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
